import { useEffect, useState } from "react";
import { api, formatCents, formatDate, formatError } from "@/lib/api";
import { PageHeader, Card, Button, Pill, Loader, EmptyState, Modal, Input, Select, Stat } from "@/components/ui/Primitives";
import { unwrap } from "@/lib/unwrap";
import { Wallet, TrendingUp, Target, Plus, Receipt, Sparkles, Printer, X } from "lucide-react";
import { toast } from "sonner";

export default function Wellness() {
  const [profile, setProfile] = useState(null);
  const [afford, setAfford] = useState(null);
  const [goals, setGoals] = useState([]);
  const [credit, setCredit] = useState(null);
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [receiptHtml, setReceiptHtml] = useState("");
  const [receiptBusy, setReceiptBusy] = useState(false);
  const [form, setForm] = useState({ name: "", target_amount: "", target_date: "", category: "savings" });

  // While the receipt overlay is open, mark <body> so print CSS shows ONLY the receipt.
  useEffect(() => {
    document.body.classList.toggle("receipt-open", !!receiptHtml);
    return () => document.body.classList.remove("receipt-open");
  }, [receiptHtml]);

  const load = async () => {
    setLoading(true);
    try {
      const [p, a, g, c, k] = await Promise.all([
        api.get("/wellness/profile").catch(() => ({ data: null })),
        api.get("/wellness/affordability").catch(() => ({ data: null })),
        api.get("/wellness/goals").catch(() => ({ data: [] })),
        api.get("/wellness/credit-impact").catch(() => ({ data: null })),
        api.get("/wellness/coach").catch(() => ({ data: null })),
      ]);
      setProfile(p.data);
      setAfford(a.data);
      setGoals(unwrap(g.data, "goals"));
      setCredit(c.data);
      setCoach(k.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const addGoal = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/wellness/goals", {
        name: form.name.trim(),
        target_amount_cents: Math.round(Number(form.target_amount || 0) * 100),
        target_date: form.target_date || null,
        category: form.category,
      });
      toast.success("Goal added");
      setOpen(false);
      setForm({ name: "", target_amount: "", target_date: "", category: "savings" });
      load();
    } catch (err) {
      toast.error(formatError(err, "Could not add goal"));
    } finally { setSaving(false); }
  };

  // Shows the receipt in an in-app overlay and prints from THIS window —
  // window.open() after an async call is always blocked on iOS Safari.
  const downloadReceipt = async () => {
    setReceiptBusy(true);
    const t = toast.loading("Preparing rent receipt…");
    try {
      const now = new Date();
      const { data } = await api.get("/wellness/rent-receipt", {
        params: { year: now.getFullYear(), month: now.getMonth() + 1 },
      });
      const html = data?.html || data?.receipt_html;
      setReceiptHtml(`
        <span class="badge">Rent receipt</span>
        ${html || renderFallback(data)}
        <div class="footer">Generated by Central Rentals Canada · contact@centralrentals.ca · 780-405-2498</div>`);
      toast.success("Receipt ready — tap Print / Save PDF", { id: t });
    } catch (err) {
      toast.error(formatError(err, "Receipt unavailable"), { id: t });
    } finally { setReceiptBusy(false); }
  };

  function renderFallback(data) {
    if (!data) return "<p class='muted'>No data available.</p>";
    const payments = data?.payments || data?.entries || data?.line_items || [];
    const rows = payments.map((p) => `<tr><td>${p.period || p.date || "—"}</td><td>${p.description || p.title || "Rent payment"}</td><td style="text-align:right">$${((p.amount_cents || 0)/100).toFixed(2)}</td></tr>`).join("");
    const total = payments.reduce((s, p) => s + (p.amount_cents || 0), 0);
    return `
      <h1>${data?.tenant_name || data?.name || ""}</h1>
      <div class="muted">${data?.address || ""}</div>
      <table>
        <thead><tr><th>Period</th><th>Description</th><th style="text-align:right">Amount</th></tr></thead>
        <tbody>${rows || '<tr><td colspan="3" class="muted">No payments on record yet.</td></tr>'}</tbody>
      </table>
      <div class="totals"><span>Total paid</span><span>$${(total/100).toFixed(2)}</span></div>
    `;
  }

  if (loading) return <Loader/>;

  const monthly = afford?.recommended_rent_cents || afford?.affordable_rent_cents || 0;

  return (
    <div>
      <PageHeader
        title="Financial wellness"
        subtitle="Affordability, credit-builder rent receipts, and goals to keep you on track."
        actions={<Button variant="secondary" onClick={downloadReceipt} disabled={receiptBusy} data-testid="wellness-receipt-button"><Receipt className="w-4 h-4"/> {receiptBusy ? "Preparing…" : "Rent receipt"}</Button>}
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Stat label="Recommended rent" value={formatCents(monthly)} sub="At ~30% income" icon={Wallet} testid="wellness-affordable"/>
        <Stat label="Credit score impact" value={credit?.score_delta != null ? `${credit.score_delta > 0 ? "+" : ""}${credit.score_delta}` : "—"} sub="Last 12 months" icon={TrendingUp} accent="text-green-800"/>
        <Stat label="Goals active" value={goals.filter((g) => !g.completed).length} sub={`${goals.filter((g) => g.completed).length} completed`} icon={Target}/>
        <Stat label="On-time payments" value={credit?.on_time_count ?? profile?.on_time_payments ?? 0} icon={Sparkles} accent="text-green-800"/>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-lg font-bold text-slate-900">Your goals</h3>
              <p className="text-sm text-slate-500">Save for security deposits, emergency funds, or moving costs.</p>
            </div>
            <Button onClick={() => setOpen(true)} data-testid="add-goal-button"><Plus className="w-4 h-4"/> Add goal</Button>
          </div>
          {goals.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">No goals yet. Add your first to start tracking.</div>
          ) : (
            <div className="space-y-3">
              {goals.map((g) => {
                const pct = g.target_amount_cents > 0 ? Math.min(100, Math.round(((g.current_amount_cents || 0) / g.target_amount_cents) * 100)) : 0;
                return (
                  <div key={g.goal_id} className="p-4 rounded-lg border border-slate-200" data-testid={`goal-${g.goal_id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="font-semibold text-slate-900">{g.name}</div>
                        <div className="text-xs text-slate-500 mt-0.5 capitalize">{g.category} {g.target_date ? `· by ${formatDate(g.target_date)}` : ""}</div>
                      </div>
                      <Pill tone={g.completed ? "green" : pct >= 75 ? "green" : pct >= 40 ? "amber" : "slate"}>{pct}%</Pill>
                    </div>
                    <div className="mt-3 h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div className="h-full bg-green-700 transition-all" style={{ width: `${pct}%` }}/>
                    </div>
                    <div className="mt-2 text-xs text-slate-500">{formatCents(g.current_amount_cents || 0)} of {formatCents(g.target_amount_cents)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>

        <Card className="p-6">
          <h3 className="font-display text-lg font-bold text-slate-900 mb-3">Credit coach</h3>
          <p className="text-sm text-slate-600 leading-relaxed">{coach?.tip || coach?.summary || "Pay your rent on time every month to build a positive payment history that can be reported to credit bureaus."}</p>
          {coach?.action_items?.length > 0 && (
            <ul className="mt-4 space-y-2 text-sm text-slate-700">
              {coach.action_items.slice(0, 4).map((a, i) => (
                <li key={i} className="flex items-start gap-2"><Sparkles className="w-3.5 h-3.5 text-green-700 mt-0.5 flex-none"/> <span>{a}</span></li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="New financial goal" testid="goal-modal">
        <form onSubmit={addGoal} className="space-y-4">
          <Input label="Goal name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Emergency fund" testid="goal-name-input"/>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Target (CAD)" required type="number" value={form.target_amount} onChange={(e) => setForm({ ...form, target_amount: e.target.value })} testid="goal-amount-input"/>
            <Input label="By date" type="date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })}/>
          </div>
          <Select label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
            <option value="savings">Savings</option>
            <option value="emergency">Emergency fund</option>
            <option value="deposit">Security deposit</option>
            <option value="moving">Moving costs</option>
            <option value="other">Other</option>
          </Select>
          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="goal-submit">{saving ? "Saving…" : "Add goal"}</Button>
          </div>
        </form>
      </Modal>

      {receiptHtml && (
        <div className="fixed inset-0 z-[100] bg-slate-900/50 overflow-y-auto p-3 sm:p-6" data-testid="receipt-overlay">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-auto my-4 print-receipt-area">
            <div className="flex items-center justify-between gap-2 px-4 sm:px-5 py-3 border-b border-slate-200 receipt-toolbar">
              <div className="font-display font-bold text-slate-900">Rent receipt</div>
              <div className="flex items-center gap-2">
                <Button onClick={() => window.print()} data-testid="receipt-print-button" className="!px-3 !py-2 text-sm">
                  <Printer className="w-4 h-4"/> Print / Save PDF
                </Button>
                <Button variant="ghost" onClick={() => setReceiptHtml("")} data-testid="receipt-close-button" className="!px-2.5 !py-2">
                  <X className="w-4 h-4"/>
                </Button>
              </div>
            </div>
            <div className="p-5 sm:p-8 receipt-doc" dangerouslySetInnerHTML={{ __html: receiptHtml }}/>
          </div>
        </div>
      )}
    </div>
  );
}
